// ── Create Time Capsule ────────────────────────────────────────────
//
// The flow, in strict order (money is never spent before it must be, and a
// capsule is NEVER created until payment is confirmed):
//
//   1. Compose: message, unlock date, visibility, optional recipient.
//   2. Review: show the exact USD price AND the exact BCH amount computed
//      from the live price, plus the wallet balance.
//   3. Pay: send $0.99-worth of BCH to the platform wallet. We AWAIT the
//      txid — the send resolves only once the wallet has broadcast it.
//   4. Seal (only after step 3 resolves): timelock-encrypt the message in
//      the browser to the drand round covering the unlock date.
//   5. Publish: ONLY the ciphertext + metadata goes to Nostr. The plaintext
//      never leaves the browser.
//
// If the browser dies between pay and publish, the seal-and-publish is
// retried automatically on the next open from a tiny local "pending" record,
// so a paid-for capsule is never lost.

import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { nip19 } from 'nostr-tools';
import {
  X, Clock, Lock, Globe, User as UserIcon, Loader2, Check,
  Wallet, ShieldCheck, AlertTriangle, Send,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useTipWallet } from '../hooks/useTipWallet';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { useCapsulePublish } from '../hooks/useTimeCapsules';
import {
  CAPSULE_TIERS, CAPSULE_WALLET, usdToSats, satsToBchStr,
  sealCapsule, newCapsuleDTag, roundForTime,
  type CapsuleVisibility,
} from '../lib/timeCapsule';

const TIER = CAPSULE_TIERS.standard;

// Quick presets for the unlock date, in seconds from now.
const DURATION_PRESETS: { label: string; secs: number }[] = [
  { label: '1 day', secs: 86400 },
  { label: '1 week', secs: 7 * 86400 },
  { label: '1 month', secs: 30 * 86400 },
  { label: '1 year', secs: 365 * 86400 },
];

// A small local record so a paid-but-unpublished capsule survives a crash.
const PENDING_KEY = 'bchnostr:capsule-pending';
interface PendingSeal {
  message: string;
  title: string;
  visibility: CapsuleVisibility;
  recipient?: string;
  unlockAt: number;
  txid: string;
  dTag: string;
}
function savePending(p: PendingSeal) {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}
function loadPending(): PendingSeal | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as PendingSeal) : null;
  } catch { return null; }
}
function clearPending() {
  try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
}

/** Resolve an npub or 64-char hex string to a hex pubkey, or null. */
function resolvePubkey(input: string): string | null {
  const v = input.trim();
  if (/^[0-9a-f]{64}$/i.test(v)) return v.toLowerCase();
  try {
    const decoded = nip19.decode(v);
    if (decoded.type === 'npub') return decoded.data;
    if (decoded.type === 'nprofile') return (decoded.data as { pubkey: string }).pubkey;
  } catch { /* not a nip19 string */ }
  return null;
}

type Stage = 'compose' | 'review' | 'paying' | 'sealing' | 'publishing' | 'done';

export default function CreateCapsuleModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated?: () => void;
}) {
  const { user, setShowLoginDialog } = useAppContext();
  const tipWallet = useTipWallet();
  const { price: bchPrice } = useBCHPrice();
  const { publishCapsule } = useCapsulePublish();

  const [stage, setStage] = useState<Stage>('compose');
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [visibility, setVisibility] = useState<CapsuleVisibility>('public');
  const [recipientInput, setRecipientInput] = useState('');
  const [durationSecs, setDurationSecs] = useState(DURATION_PRESETS[2].secs); // 1 month
  const [customDate, setCustomDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [recovering, setRecovering] = useState(false);

  // ── Pricing ──────────────────────────────────────────────────────
  const priceSats = useMemo(() => usdToSats(TIER.priceUsd, bchPrice), [bchPrice]);
  const bchAmount = priceSats != null ? satsToBchStr(priceSats) : null;
  const walletBalance = Number(tipWallet.balanceSats);
  const canAfford = priceSats != null && walletBalance >= priceSats;

  // ── Unlock time ──────────────────────────────────────────────────
  const unlockAt = useMemo(() => {
    if (customDate) {
      const t = Math.floor(new Date(customDate).getTime() / 1000);
      if (Number.isFinite(t) && t > 0) return t;
    }
    return Math.floor(Date.now() / 1000) + durationSecs;
  }, [customDate, durationSecs]);

  const unlockTooSoon = unlockAt <= Math.floor(Date.now() / 1000) + 60;
  const unlockTooFar = unlockAt > Math.floor(Date.now() / 1000) + TIER.maxDurationSecs;

  const recipientPubkey = visibility === 'addressed' ? resolvePubkey(recipientInput) : null;
  const recipientInvalid = visibility === 'addressed' && recipientInput.trim() !== '' && !recipientPubkey;

  const composeValid =
    message.trim().length > 0 &&
    message.length <= TIER.maxChars &&
    title.trim().length > 0 &&
    !unlockTooSoon &&
    !unlockTooFar &&
    (visibility !== 'addressed' || !!recipientPubkey);

  // ── Recover a paid-but-unpublished capsule on mount ────────────────
  // Warm up the wallet's send engine as soon as the modal opens, so it's
  // ready by the time the user reaches "Pay & seal". Non-blocking.
  useEffect(() => {
    if (user && !tipWallet.ready) tipWallet.init().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const pending = loadPending();
    if (!pending || !user || pending.recipient === undefined) {
      // still attempt recovery even without recipient; guard below handles it
    }
    if (pending && user) {
      setRecovering(true);
      (async () => {
        try {
          const { ciphertext, round } = await sealCapsule(pending.message, pending.unlockAt);
          await publishCapsule({
            dTag: pending.dTag,
            title: pending.title,
            visibility: pending.visibility,
            recipient: pending.recipient,
            unlockAt: pending.unlockAt,
            round,
            ciphertext,
            txid: pending.txid,
          });
          clearPending();
          setStage('done');
          onCreated?.();
        } catch {
          // Leave the pending record in place to retry next time.
        } finally {
          setRecovering(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── The paid seal-and-publish pipeline ─────────────────────────────
  const handleConfirmAndPay = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    if (!composeValid || priceSats == null) return;
    if (!canAfford) {
      setError('Not enough BCH in your wallet to seal this capsule.');
      return;
    }
    setError(null);
    const dTag = newCapsuleDTag();

    // ── Step 1: PAY. Nothing is created until this resolves. ──
    setStage('paying');
    let txid: string;
    try {
      // The cached balance shows instantly, but the wallet's send engine
      // (mainnet-js) initializes lazily. Spin it up on demand before sending
      // — exactly like the tip / boost / pay-business flows do — so a fresh
      // session doesn't refuse a payment the user can clearly afford.
      if (!tipWallet.ready) await tipWallet.init();
      txid = await tipWallet.sendBCH(CAPSULE_WALLET, BigInt(priceSats));
    } catch (e) {
      setStage('review');
      setError(e instanceof Error ? e.message : 'Payment failed. No capsule was created.');
      return;
    }
    tipWallet.refreshBalance();

    // Persist a recovery record: the payment is done, so we MUST end up with
    // a published capsule even if the next steps are interrupted.
    savePending({
      message, title, visibility,
      recipient: recipientPubkey || undefined,
      unlockAt, txid, dTag,
    });

    // ── Step 2: SEAL (timelock-encrypt) in the browser. ──
    setStage('sealing');
    let ciphertext: string;
    let round: number;
    try {
      const sealed = await sealCapsule(message, unlockAt);
      ciphertext = sealed.ciphertext;
      round = sealed.round;
    } catch (e) {
      setStage('review');
      setError(
        (e instanceof Error ? e.message : 'Could not seal the capsule.') +
        ' Your payment went through — reopen Time Capsules to finish sealing.',
      );
      return;
    }

    // ── Step 3: PUBLISH only the ciphertext. ──
    setStage('publishing');
    try {
      await publishCapsule({
        dTag, title, visibility,
        recipient: recipientPubkey || undefined,
        unlockAt, round, ciphertext, txid,
      });
      clearPending();
      setStage('done');
      onCreated?.();
    } catch (e) {
      setStage('review');
      setError(
        (e instanceof Error ? e.message : 'Publishing failed.') +
        ' Your payment went through — reopen Time Capsules to finish.',
      );
    }
  };

  const busy = stage === 'paying' || stage === 'sealing' || stage === 'publishing' || recovering;

  const unlockDate = new Date(unlockAt * 1000);

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={busy ? undefined : onClose}>
      <div className="bg-card border border-border rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border">
          <h2 className="font-bold text-sm flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Clock className="w-4 h-4 text-white" />
            </div>
            Create a Time Capsule
          </h2>
          {!busy && (
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {recovering ? (
            <div className="text-center py-10 space-y-3">
              <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto" />
              <p className="text-sm font-semibold">Finishing a paid capsule…</p>
              <p className="text-xs text-muted-foreground">Sealing and publishing your previous capsule.</p>
            </div>
          ) : stage === 'done' ? (
            <div className="text-center py-8 space-y-3">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 text-emerald-500 flex items-center justify-center mx-auto">
                <Check className="w-7 h-7" />
              </div>
              <p className="text-sm font-bold">Capsule sealed!</p>
              <p className="text-xs text-muted-foreground leading-relaxed px-2">
                Your message is locked until <strong className="text-foreground">{unlockDate.toLocaleString()}</strong>.
                No one — not even you or BCHNostr — can read it until then.
              </p>
              <button onClick={onClose} className="mt-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
                Done
              </button>
            </div>
          ) : stage === 'compose' ? (
            <>
              {/* Title */}
              <div>
                <label className="text-[11px] font-medium text-muted-foreground">Public title</label>
                <input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="A letter to my future self"
                  maxLength={140}
                  className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Always visible. The message itself stays sealed.</p>
              </div>

              {/* Message */}
              <div>
                <label className="text-[11px] font-medium text-muted-foreground">Sealed message</label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Write something for the future…"
                  maxLength={TIER.maxChars}
                  rows={5}
                  className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <p className="text-[10px] text-muted-foreground mt-1 text-right">{message.length} / {TIER.maxChars}</p>
              </div>

              {/* Visibility */}
              <div>
                <label className="text-[11px] font-medium text-muted-foreground">Who is it for?</label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {([
                    { id: 'public', label: 'Public', icon: Globe, desc: 'Anyone' },
                    { id: 'addressed', label: 'Someone', icon: UserIcon, desc: 'One person' },
                    { id: 'private', label: 'Private', icon: Lock, desc: 'Just me' },
                  ] as const).map(opt => {
                    const Icon = opt.icon;
                    const active = visibility === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => setVisibility(opt.id)}
                        className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2.5 text-xs transition-colors ${
                          active ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted text-muted-foreground'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="font-medium">{opt.label}</span>
                        <span className="text-[9px] opacity-70">{opt.desc}</span>
                      </button>
                    );
                  })}
                </div>
                {visibility === 'addressed' && (
                  <div className="mt-2">
                    <input
                      value={recipientInput}
                      onChange={e => setRecipientInput(e.target.value)}
                      placeholder="Recipient npub1… or hex pubkey"
                      className="w-full bg-background rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    {recipientInvalid && (
                      <p className="text-[10px] text-red-500 mt-1">That doesn’t look like a valid npub or pubkey.</p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-1">
                      The recipient will see it appear in their received capsules and can open it once it unlocks.
                    </p>
                  </div>
                )}
              </div>

              {/* Unlock date */}
              <div>
                <label className="text-[11px] font-medium text-muted-foreground">Unlock date</label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {DURATION_PRESETS.map(p => (
                    <button
                      key={p.label}
                      onClick={() => { setDurationSecs(p.secs); setCustomDate(''); }}
                      className={`px-2.5 py-1 rounded-lg text-xs border transition-colors ${
                        !customDate && durationSecs === p.secs
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-border hover:bg-muted text-muted-foreground'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <input
                  type="datetime-local"
                  value={customDate}
                  onChange={e => setCustomDate(e.target.value)}
                  className="w-full bg-background rounded-lg px-3 py-2 text-xs mt-2 focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Unlocks {unlockDate.toLocaleString()}
                </p>
                {unlockTooSoon && <p className="text-[10px] text-red-500 mt-1">Pick a time at least a minute in the future.</p>}
                {unlockTooFar && <p className="text-[10px] text-red-500 mt-1">Standard capsules unlock within 5 years.</p>}
              </div>

              <button
                onClick={() => { if (composeValid) { setError(null); setStage('review'); } }}
                disabled={!composeValid}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50"
              >
                Review &amp; seal
              </button>
            </>
          ) : (
            // ── Review / pay / progress ──
            <>
              <div className="rounded-xl border border-border bg-muted/40 p-4 space-y-2">
                <p className="text-xs font-semibold flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> {title || 'Untitled capsule'}</p>
                <div className="text-[11px] text-muted-foreground space-y-1">
                  <div className="flex justify-between"><span>Visibility</span><span className="text-foreground capitalize">{visibility}</span></div>
                  {visibility === 'addressed' && recipientPubkey && (
                    <div className="flex justify-between"><span>Recipient</span><span className="text-foreground font-mono">{nip19.npubEncode(recipientPubkey).slice(0, 14)}…</span></div>
                  )}
                  <div className="flex justify-between"><span>Unlocks</span><span className="text-foreground">{unlockDate.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span>drand round</span><span className="text-foreground font-mono">{roundForTime(unlockAt).toLocaleString()}</span></div>
                </div>
              </div>

              {/* Price — USD and exact BCH, shown before payment */}
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Sealing fee</span>
                  <span className="text-lg font-bold">${TIER.priceUsd.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">You pay</span>
                  <span className="text-sm font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                    {bchAmount != null ? `${bchAmount} BCH` : '—'}
                  </span>
                </div>
                {priceSats != null && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground/70">≈ {priceSats.toLocaleString()} sats</span>
                    <span className="text-[10px] text-muted-foreground/70">
                      {bchPrice != null ? `@ $${bchPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}/BCH` : 'price unavailable'}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 pt-1 text-[10px] text-muted-foreground border-t border-emerald-500/20">
                  <Wallet className="w-3 h-3" />
                  Wallet balance: {walletBalance.toLocaleString()} sats
                  {!canAfford && priceSats != null && <span className="text-red-500 font-medium">· insufficient</span>}
                </div>
              </div>

              {bchPrice == null && (
                <p className="text-[11px] text-amber-600 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> BCH price is temporarily unavailable — try again in a moment.
                </p>
              )}

              {/* Progress indicator during the pipeline */}
              {busy && (
                <div className="rounded-lg bg-muted/60 p-3 space-y-1.5 text-xs">
                  <StageRow done={stage !== 'paying'} active={stage === 'paying'} label="Sending BCH payment" />
                  <StageRow done={stage === 'publishing' || stage === 'done'} active={stage === 'sealing'} label="Timelock-encrypting your message" />
                  <StageRow done={stage === 'done'} active={stage === 'publishing'} label="Publishing sealed capsule" />
                </div>
              )}

              {error && (
                <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {error}
                </p>
              )}

              <div className="flex gap-2">
                {!busy && (
                  <button
                    onClick={() => { setError(null); setStage('compose'); }}
                    className="px-4 py-3 rounded-xl border border-border text-sm text-muted-foreground hover:text-foreground"
                  >
                    Back
                  </button>
                )}
                <button
                  onClick={handleConfirmAndPay}
                  disabled={busy || !canAfford || priceSats == null}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {stage === 'paying' ? 'Paying…'
                    : stage === 'sealing' ? 'Sealing…'
                    : stage === 'publishing' ? 'Publishing…'
                    : bchAmount != null ? `Pay ${bchAmount} BCH & seal` : 'Seal capsule'}
                </button>
              </div>

              <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
                Your message is encrypted in your browser <strong>after</strong> payment. Only the encrypted capsule is
                published — the plain text is never uploaded or stored anywhere.
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function StageRow({ done, active, label }: { done: boolean; active: boolean; label: string }) {
  return (
    <div className={`flex items-center gap-2 ${done ? 'text-emerald-500' : active ? 'text-foreground' : 'text-muted-foreground/50'}`}>
      {done ? <Check className="w-3.5 h-3.5" /> : active ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <div className="w-3.5 h-3.5 rounded-full border border-current" />}
      <span>{label}</span>
    </div>
  );
}
